import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { Investor, EpoRule, EpdRule, EpoRuleType } from '@/types/investor';
import { createEpoRule, createEpdRule, getValidEpoTypes, getValidEpdTypes, normalizeRuleValue } from '@/utils/investor/businessRules';
import type { PolicySectionsProps } from './types';

export function InvestorPolicySections({
  investor,
  draft,
  editingSection,
  hasWholesale,
  hasCorrespondent,
  updateDraft,
  sectionActions,
  isStrictAdmin,
  netWorthRows,
  toggleDelegation,
  updateNetWorthRow,
  addNetWorthRow,
  removeNetWorthRow
}: PolicySectionsProps) {
  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const formatCurrencyOrEmpty = (n?: number) =>
    typeof n === 'number' ? formatCurrency(n) : 'Not set';

  const formatPercentOrEmpty = (n?: number | null) =>
    typeof n === 'number' ? `${n}%` : 'Not set';

  const formatRuleDescription = (rule?: { description?: string }) =>
    rule?.description?.trim() || 'Not set';

  // Structured EPO/EPD rule editor (type + positive value). Shared by the
  // wholesale and correspondent policy cards so both produce a normalizer-valid
  // rule via create*Rule (and a value-less rule when cleared).
  type RuleKey = 'wholesaleEpoRule' | 'wholesaleEpdRule' | 'correspondentEpoRule' | 'correspondentEpdRule';

  // Item 6: a Delegated / Non-Delegated checkbox grid over the investor's correspondent products.
  const renderDelegationLevel = (level: 'delegated' | 'nonDelegated', label: string) => {
    const products = draft.correspondentProducts ?? [];
    const selected = new Set((draft.delegation ?? { delegated: [], nonDelegated: [] })[level]);
    return (
      <div className="lw-product-editor-channel c">
        <div className="lw-product-editor-head">
          <span className="lw-channel-row-label">{label}</span>
          <span className="lw-product-editor-count">{selected.size} selected</span>
        </div>
        <div className="lw-product-checkbox-grid">
          {products.length === 0 ? (
            <span className="lw-kv-value muted">No correspondent products</span>
          ) : products.map((product) => (
            <label key={`${level}-${product}`} className="lw-product-checkbox">
              <input type="checkbox" checked={selected.has(product)} onChange={() => toggleDelegation(level, product)} />
              <span>{product}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderRuleEditor = (
    label: string,
    channel: 'wholesale' | 'correspondent',
    kind: 'epo' | 'epd',
    ruleKey: RuleKey
  ) => {
    const rule = draft[ruleKey] as EpoRule | EpdRule | undefined;
    const types = kind === 'epo' ? getValidEpoTypes(channel) : getValidEpdTypes(channel);
    const currentType = (rule?.type ?? 'payments') as EpoRuleType;
    const isCalendarMonthRule = kind === 'epo' && currentType === 'calendar_month_end_after_purchase';
    const buildRule = (type: EpoRuleType, value: number | undefined) => {
      if (kind === 'epo') {
        return createEpoRule(channel, type, value);
      }
      if (type === 'calendar_month_end_after_purchase') {
        throw new Error('Calendar-month rules are only supported for EPO');
      }
      return createEpdRule(channel, type, value);
    };
    return (
      <div className="lw-field">
        <span className="lw-label">{label}</span>
        <div style={{ display: 'flex', gap: 8, flexDirection: isCalendarMonthRule ? 'column' : 'row' }}>
          <select
            className="lw-perpage"
            style={{ minWidth: 0, maxWidth: '100%' }}
            aria-label={`${label} type`}
            value={currentType}
            onChange={(e) => updateDraft(ruleKey, buildRule(e.target.value as EpoRuleType, normalizeRuleValue(rule?.value)))}
          >
            {types.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {isCalendarMonthRule && <label className="lw-label" htmlFor={`${ruleKey}-months`}>Calendar months after purchase</label>}
          <Input
            id={isCalendarMonthRule ? `${ruleKey}-months` : undefined}
            className="lw-input"
            type="number"
            min="1"
            step="1"
            max={currentType === 'payments' ? 12 : undefined}
            aria-label={isCalendarMonthRule ? `${label} calendar months after purchase` : `${label} value`}
            placeholder="Value"
            value={rule?.value || ''}
            onChange={(e) => updateDraft(ruleKey, buildRule(currentType, normalizeRuleValue(e.target.value)))}
          />
        </div>
        <span className="text-xs text-muted-foreground">{rule?.description || 'Configure above'}{isCalendarMonthRule ? ' The purchase month is not counted.' : ''}</span>
      </div>
    );
  };

  return (
    <>
      {hasWholesale && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">Compensation & Fees</h4>
            {sectionActions('compensation', ['wholesaleLenderPaidComp', 'wholesaleAdditionalLendwiseFee'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'compensation' ? (
              <div className="lw-edit-grid">
                <label className="lw-field">
                  <span className="lw-label">LPC %</span>
                  <Input className="lw-input" type="number" min="1" max="2.5" step="0.01" value={draft.wholesaleLenderPaidComp ?? ''} onChange={(e) => updateDraft('wholesaleLenderPaidComp', e.target.value === '' ? null : Number(e.target.value))} />
                </label>
                <label className="lw-field">
                  <span className="lw-label">Additional Lender Fee</span>
                  <Input className="lw-input" type="number" min="0" step="1" value={draft.wholesaleAdditionalLendwiseFee ?? ''} onChange={(e) => updateDraft('wholesaleAdditionalLendwiseFee', e.target.value === '' ? null : Number(e.target.value))} />
                </label>
              </div>
            ) : (
              <div className="lw-edit-grid">
                <div><div className="lw-label">LPC</div><div className="text-sm text-foreground">{formatPercentOrEmpty(investor.wholesaleLenderPaidComp)}</div></div>
                <div><div className="lw-label">Additional Lender Fee</div><div className="text-sm text-foreground">{formatCurrencyOrEmpty(investor.wholesaleAdditionalLendwiseFee ?? undefined)}</div></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasWholesale && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">EPO / EPD Policy (Wholesale)</h4>
            {sectionActions('wholesaleRules', ['wholesaleEpoRule', 'wholesaleEpdRule'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'wholesaleRules' ? (
              <div className="lw-edit-grid">
                {renderRuleEditor('Wholesale EPO', 'wholesale', 'epo', 'wholesaleEpoRule')}
                {renderRuleEditor('Wholesale EPD', 'wholesale', 'epd', 'wholesaleEpdRule')}
              </div>
            ) : (
              <div className="lw-edit-grid">
                <div><div className="lw-label">Wholesale EPO</div><div className="text-sm text-foreground">{formatRuleDescription(investor.wholesaleEpoRule)}</div></div>
                <div><div className="lw-label">Wholesale EPD</div><div className="text-sm text-foreground">{formatRuleDescription(investor.wholesaleEpdRule)}</div></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasCorrespondent && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">EPO / EPD Policy (Correspondent)</h4>
            {sectionActions('rules', ['correspondentEpoRule', 'correspondentEpdRule'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'rules' ? (
              <div className="lw-edit-grid">
                {renderRuleEditor('Correspondent EPO', 'correspondent', 'epo', 'correspondentEpoRule')}
                {renderRuleEditor('Correspondent EPD', 'correspondent', 'epd', 'correspondentEpdRule')}
              </div>
            ) : (
              <div className="lw-edit-grid">
                <>
                  <div><div className="lw-label">Correspondent EPO</div><div className="text-sm text-foreground">{formatRuleDescription(investor.correspondentEpoRule)}</div></div>
                  <div><div className="lw-label">Correspondent EPD</div><div className="text-sm text-foreground">{formatRuleDescription(investor.correspondentEpdRule)}</div></div>
                </>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasCorrespondent && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">Clarifii Reliance Letter</h4>
            {sectionActions('clarifii', ['clarifiRelianceRequired', 'clarifiRelianceInvestorRecipients'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'clarifii' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label className="lw-field"><span className="lw-label">Reliance letter from Clarifii required?</span><input type="checkbox" checked={Boolean(draft.clarifiRelianceRequired)} onChange={(e) => updateDraft('clarifiRelianceRequired', e.target.checked)} /></label>
                {draft.clarifiRelianceRequired && (
                  <>
                    <div className="text-xs text-muted-foreground" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div>Fixed TO: reliance_support@clarifii.com, Adam.Peat@clarifii.com</div>
                      <div>CC: jeremy@lendwisemtg.com</div>
                    </div>
                    <label className="lw-field">
                      <span className="lw-label">Investor-specific TO recipients (one per line, at least one required)</span>
                      <Textarea
                        className="lw-input"
                        rows={3}
                        style={{ height: 'auto', resize: 'vertical' }}
                        aria-label="Clarifii investor recipients"
                        value={(draft.clarifiRelianceInvestorRecipients ?? []).join('\n')}
                        onChange={(e) => updateDraft('clarifiRelianceInvestorRecipients', e.target.value.split('\n'))}
                        placeholder={'recipient1@example.com\nrecipient2@example.com'}
                      />
                    </label>
                  </>
                )}
              </div>
            ) : (
              investor.clarifiRelianceRequired ? (
                <div>
                  <div className="text-sm text-foreground">Required</div>
                  {(investor.clarifiRelianceInvestorRecipients?.length ?? 0) > 0 && (
                    <div className="text-xs text-muted-foreground">TO: {(investor.clarifiRelianceInvestorRecipients ?? []).join(', ')}</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Not required</div>
              )
            )}
          </CardContent>
        </Card>
      )}

      {hasCorrespondent && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">Delegation Level</h4>
            {sectionActions('delegation', ['delegation'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'delegation' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {renderDelegationLevel('delegated', 'Delegated')}
                {renderDelegationLevel('nonDelegated', 'Non-Delegated')}
              </div>
            ) : (() => {
              const del = investor.delegation ?? { delegated: [], nonDelegated: [] };
              if (del.delegated.length === 0 && del.nonDelegated.length === 0) {
                // No product-level delegation set — fall back to the legacy single value for display.
                return <div className="text-sm text-muted-foreground italic">{investor.delegatedType || 'Not set'}</div>;
              }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {del.delegated.length > 0 && (
                    <div className="text-sm text-foreground"><span className="lw-label">Delegated:</span> {del.delegated.join(', ')}</div>
                  )}
                  {del.nonDelegated.length > 0 && (
                    <div className="text-sm text-foreground"><span className="lw-label">Non-Delegated:</span> {del.nonDelegated.join(', ')}</div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <Card className="lw-section-card">
        <CardHeader className="lw-section-head flex-row space-y-0">
          <h4 className="lw-section-title">Optimal Blue</h4>
          {(hasCorrespondent || isStrictAdmin) && sectionActions('optimal-blue', [
            ...(isStrictAdmin ? ['wholesaleCanIntegrateOptimalBlue', 'wholesaleHasIntegratedOptimalBlue'] as Array<keyof Investor> : []),
            ...(hasCorrespondent ? ['correspondentCanIntegrateOptimalBlue', 'correspondentHasIntegratedOptimalBlue'] as Array<keyof Investor> : [])
          ])}
        </CardHeader>
        <CardContent className="lw-section-content">
          {editingSection === 'optimal-blue' ? (
            <div className="lw-edit-grid">
              {hasWholesale && isStrictAdmin && (
                <>
                  <label className="lw-field"><span className="lw-label">Wholesale can integrate</span><input type="checkbox" checked={Boolean(draft.wholesaleCanIntegrateOptimalBlue)} onChange={(e) => updateDraft('wholesaleCanIntegrateOptimalBlue', e.target.checked)} /></label>
                  <label className="lw-field"><span className="lw-label">Wholesale integrated</span><input type="checkbox" checked={Boolean(draft.wholesaleHasIntegratedOptimalBlue)} onChange={(e) => updateDraft('wholesaleHasIntegratedOptimalBlue', e.target.checked)} /></label>
                </>
              )}
              {hasCorrespondent && (
                <div className="lw-field">
                  <span className="lw-label">Correspondent pricing</span>
                  <div className="lw-seg">
                    <button
                      type="button"
                      className={draft.correspondentHasIntegratedOptimalBlue ? 'active' : ''}
                      onClick={() => {
                        updateDraft('correspondentCanIntegrateOptimalBlue', true);
                        updateDraft('correspondentHasIntegratedOptimalBlue', true);
                      }}
                    >
                      Pricing Active
                    </button>
                    <button
                      type="button"
                      className={!draft.correspondentHasIntegratedOptimalBlue ? 'active' : ''}
                      onClick={() => {
                        updateDraft('correspondentCanIntegrateOptimalBlue', true);
                        updateDraft('correspondentHasIntegratedOptimalBlue', false);
                      }}
                    >
                      See Lock Desk
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="lw-edit-grid">
              {investor.canDoWholesale && <div><div className="lw-label">Wholesale</div><div className="text-sm text-foreground">Do not use OB - see Rate Sheet</div></div>}
              {investor.canGoCorrespondent && <div><div className="lw-label">Correspondent</div><div className="text-sm text-foreground">{investor.correspondentHasIntegratedOptimalBlue ? 'Pricing Active' : 'See Lock Desk'}</div></div>}
            </div>
          )}
        </CardContent>
      </Card>

      {hasCorrespondent && (
        <Card className="lw-section-card">
          <CardHeader className="lw-section-head flex-row space-y-0">
            <h4 className="lw-section-title">Net Worth Requirements</h4>
            {sectionActions('net-worth', ['netWorthRequirementRows'])}
          </CardHeader>
          <CardContent className="lw-section-content">
            {editingSection === 'net-worth' ? (
              <div className="space-y-2">
                {(draft.netWorthRequirementRows ?? []).map((row, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_1fr_auto]">
                    <Input aria-label={`Net worth requirement ${index + 1}`} className="lw-input" value={row.label ?? ''} onChange={(e) => updateNetWorthRow(index, 'label', e.target.value)} placeholder="Requirement" />
                    <Input aria-label={`Net worth amount ${index + 1}`} className="lw-input" type="number" min="0" step="1" value={row.amount ?? ''} onChange={(e) => updateNetWorthRow(index, 'amount', e.target.value)} placeholder="Amount" />
                    <Input aria-label={`Net worth notes ${index + 1}`} className="lw-input" value={row.notes ?? ''} onChange={(e) => updateNetWorthRow(index, 'notes', e.target.value)} placeholder="Notes" />
                    <button type="button" className="lw-btn lw-btn-ghost sm" onClick={() => removeNetWorthRow(index)}>Remove</button>
                  </div>
                ))}
                <button type="button" className="lw-btn lw-btn-secondary sm" onClick={addNetWorthRow}>Add row</button>
              </div>
            ) : (
              <div className="space-y-1">
                {netWorthRows.length > 0 ? netWorthRows.map((row, i) => (
                  <div key={i} className="flex justify-between gap-4 text-sm">
                    <span className="text-muted-foreground">{row.label || 'Requirement'}</span>
                    <span className="text-right text-foreground">
                      {typeof row.amount === 'number' ? formatCurrency(row.amount) : '—'}
                      {row.notes ? <span className="ml-2 text-xs text-muted-foreground">{row.notes}</span> : null}
                    </span>
                  </div>
                )) : (
                  <span className="text-sm text-muted-foreground">Not set</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
